import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FaShieldAlt,
  FaBolt,
  FaNetworkWired,
  FaHeadset,
  FaArrowRight,
} from "react-icons/fa";
import { ALL_DATA_CENTERS } from "../../constant/AllDataCenters";
import { COMPANY_DATA } from "../../constant/CompanyData";
import Badge from "../common/Badge";
import Eyebrow from "../common/Eyebrow";

const FEATURE_ICONS = [FaShieldAlt, FaBolt, FaNetworkWired, FaHeadset];

const DataCenterCity = () => {
  const { city } = useParams();
  const cityData = ALL_DATA_CENTERS[city.toLowerCase()];

  if (!cityData) {
    return (
      <>
        <Helmet>
          <title>City Not Found - QuantaRock</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 py-24 text-center">
          <Eyebrow className="text-sm">404</Eyebrow>
          <h1 className="mt-4 text-3xl font-bold text-[var(--website-primary-color)] sm:text-4xl">
            City not found
          </h1>
          <p className="mt-4 max-w-md text-slate-600">
            We don't have a data center page for that location yet.
          </p>
          <Link
            to="/data-centers"
            className="mt-8 rounded-lg bg-[var(--website-primary-color)] px-6 py-3 font-semibold text-white transition hover:opacity-90"
          >
            View all cities
          </Link>
        </div>
      </>
    );
  }

  const jsonLdLocalBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `QuantaRock Systems - Data Centers in ${cityData.city}`,
    url: `https://quantarock.in/data-centers/${city.toLowerCase()}`,
    description: cityData.metaDescription,
    telephone: COMPANY_DATA.phoneNumber,
    email: COMPANY_DATA.mailID,
    areaServed: {
      "@type": "City",
      name: cityData.city,
      containedInPlace: {
        "@type": "State",
        name: cityData.state,
        containedInPlace: {
          "@type": "Country",
          name: "India",
        },
      },
    },
    serviceType: "Data Center Services",
  };

  return (
    <>
      <Helmet>
        <title>{cityData.title}</title>
        <meta name="description" content={cityData.metaDescription} />
        <meta name="keywords" content={cityData.keywords} />
        <meta property="og:title" content={cityData.title} />
        <meta property="og:description" content={cityData.metaDescription} />
        <meta
          property="og:url"
          content={`https://quantarock.in/data-centers/${city.toLowerCase()}`}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://quantarock.in/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={cityData.title} />
        <meta name="twitter:description" content={cityData.metaDescription} />
        <meta
          name="twitter:image"
          content="https://quantarock.in/og-image.jpg"
        />
        <link
          rel="canonical"
          href={`https://quantarock.in/data-centers/${city.toLowerCase()}`}
        />
        <script type="application/ld+json">
          {JSON.stringify(jsonLdLocalBusiness)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero */}
        <section className="relative w-full overflow-hidden bg-[var(--surface-dark)] py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--surface-dark)] via-[var(--surface-dark)] to-[var(--surface-dark)]" />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="relative z-10 mx-auto max-w-4xl px-6 text-center"
          >
            <Badge>{cityData.state}, India</Badge>
            <h1
              style={{
                textShadow:
                  "0 0 1px rgba(0,0,0,1), 0 0 3px rgba(0,0,0,1), 0 1px 4px rgba(0,0,0,0.95), 0 2px 10px rgba(0,0,0,0.9), 0 6px 20px rgba(0,0,0,0.7)",
              }}
              className="text-3xl font-bold leading-[1.15] text-white sm:text-4xl md:text-5xl"
            >
              {cityData.h1}
            </h1>
            <p
              style={{
                textShadow:
                  "0 0 1px rgba(0,0,0,1), 0 0 2px rgba(0,0,0,1), 0 1px 3px rgba(0,0,0,0.9), 0 4px 12px rgba(0,0,0,0.75)",
              }}
              className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
            >
              {cityData.intro}
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center justify-center rounded-full bg-[var(--website-secondary-color)] px-8 py-4 text-sm font-semibold text-[var(--on-secondary)] shadow-lg shadow-[rgba(var(--website-secondary-rgb),0.2)] transition hover:scale-[1.02] hover:brightness-110"
            >
              {cityData.cta}
            </Link>
          </motion.div>
        </section>

        {/* Features */}
        <section className="bg-white px-6 py-20">
          <div className="mx-auto max-w-6xl">
            <div className="text-center">
              <Eyebrow>Why QuantaRock</Eyebrow>
            </div>
            <h2 className="mx-auto mt-3 max-w-3xl text-center text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
              Why choose our {cityData.city} data center
            </h2>

            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {cityData.features.map((feature, index) => {
                const Icon = FEATURE_ICONS[index % FEATURE_ICONS.length];
                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[rgba(var(--website-secondary-rgb),0.6)] hover:shadow-lg"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--website-primary-color)] text-[var(--website-secondary-color)] transition group-hover:bg-[var(--website-secondary-color)] group-hover:text-[var(--website-primary-color)]">
                      <Icon size={20} />
                    </div>
                    <h3 className="mt-5 text-lg font-semibold text-[var(--website-primary-color)]">
                      {feature.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">
                      {feature.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="bg-[var(--surface-dark)] px-6 py-20">
          <div className="mx-auto max-w-6xl">
            <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-[var(--website-secondary-color)]">
              Services
            </p>
            <h2 className="mx-auto mt-3 max-w-3xl text-center text-3xl font-bold tracking-tight text-white md:text-4xl">
              Data center services in {cityData.city}
            </h2>

            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cityData.services.map((service, index) => (
                <motion.div
                  key={service}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center transition hover:border-[rgba(var(--website-secondary-rgb),0.4)] hover:bg-white/10"
                >
                  <h3 className="text-base font-semibold text-white">
                    {service}
                  </h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Coverage */}
        <section className="bg-slate-50 px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-2xl rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm md:p-10"
          >
            <h2 className="text-2xl font-bold text-[var(--website-primary-color)] md:text-3xl">
              Serving {cityData.city}, {cityData.state}
            </h2>
            <p className="mt-4 text-slate-600">
              Get in touch to discuss availability, pricing, and deployment
              timelines for {cityData.city}.
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--website-primary-color)] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[var(--primary-hover)]"
            >
              Contact our team
              <FaArrowRight size={12} />
            </Link>
          </motion.div>
        </section>

        {/* CTA */}
        <section className="bg-white px-6 py-16">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 rounded-3xl bg-[var(--surface-dark)] px-8 py-14 text-center">
            <h2 className="text-2xl font-bold text-white md:text-3xl">
              Ready to power your business in {cityData.city}?
            </h2>
            <p className="max-w-xl text-slate-300">
              Contact QuantaRock Systems today to learn more about our data
              center solutions in {cityData.city} and how we can support your
              infrastructure needs.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-full bg-[var(--website-secondary-color)] px-8 py-4 text-sm font-semibold text-[var(--on-secondary)] shadow-lg shadow-[rgba(var(--website-secondary-rgb),0.2)] transition hover:scale-[1.02] hover:brightness-110"
            >
              Get started now
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default DataCenterCity;
